'use client';
import { useRef, useEffect, useState, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import useSWRInfinite from 'swr/infinite';
import Image from 'next/image';
import Link from 'next/link';
import { ChevronUp, Expand, Move, Gem, Image as ImageIcon, Video } from 'lucide-react';
import CardWrapper from '../../shared/pictureCard/cardWrapper';
import { fetcher } from '@/lib/api/fetcher';
import { CategoryText } from './CategoryText';
// import { ValidLocale } from "i18n";
import { useDisplayModeContext } from '../../../app/context/displayModeStore';
import { useGlobalContext } from '../../../app/context/store';
import { getLocalizedPath } from '../../shared/formatURL';
import { getUsernameById } from '@/lib/api/user';
import cookie from 'cookie';
import { TabLink } from '@/components/shared/tabLink';
import ScrollButton from '@/components/shared/infiniteScroll/scrollButton';
import { gifReady } from '@/components/shared/generators/generators';

interface Post {
  _id: string;
  author: string;
  createdAt: string;
  imageUrl: string;
  videoUrl: string;
  thumbnailUrl: string;
  likes: number;
  model: string;
  tagGroups: string[];
  updatedAt: string;
  userLiked: boolean;
  views: number;
}

interface PageData {
  info: {
    count: number;
    next: string | null;
    pages: number;
    prev: string | null;
  };
  results: Post[];
  username?: string;
}

export function InfiniteScrollWrapper({
  modalTranslations,
  translations,
  signInModalTranslations
}: {
  modalTranslations: any;
  translations: any;
  signInModalTranslations: any;
}) {
  const { user } = useGlobalContext();
  const { state, dispatch } = useDisplayModeContext();
  const [sliderValue, setSliderValue] = useState(1);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(6);
  // const [userName, setUserName] = useState("");
  const [fullScreen, setFullScreen] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const pathChunk = pathname.split('/');
  const localeKey = translations.key; //`${pathChunk[1]}-${pathChunk[2].toUpperCase()}`;
  let _id: any = null;
  if (localeKey == 'en-US') {
    _id = pathChunk[2];
  } else {
    _id = pathChunk[4];
  }
  const [tagData, setTagData] = useState<any>({ username: _id });
  // State hooks for current tab, generator, and scroll visibility
  const [currTab, setCurrTab] = useState(searchParams.get('filter') || 'top');
  const [contentType, setContentType] = useState<'image' | 'gif'>(
    (searchParams.get('type') as 'image' | 'gif') || 'image'
  );

  const [currGenerators, setCurrGenerators] = useState(
    (searchParams.get('style')?.split(',') || ['all']) as string[]
  );

  const [showScrollTop, setShowScrollTop] = useState(false);

  // Derived states
  const queryParam = searchParams.get('search');
  const newURL = translations.path + `user/${_id}`;
  const baseEndpoint =
    contentType === 'gif'
      ? `${process.env.NEXT_PUBLIC_BACKEND_URL}/post/profile-gifs`
      : `${process.env.NEXT_PUBLIC_BACKEND_URL}/post/collection`;
  // const searchEndpoint = `${process.env.NEXT_PUBLIC_BACKEND_URL}/post/search`;

  const constructUrl = (
    tab: string,
    generator: string,
    index: number,
    tagId: string = ''
  ): string => {
    // For GIFs, use simpler params
    if (contentType === 'gif') {
      const baseParams = `user=${_id}&include=userLikes`;
      // GIFs only support 'new' and 'top', no 'hot'
      const sort = tab === 'top' ? 'top' : 'new';
      return `${baseEndpoint}?${baseParams}&sort=${sort}`;
    }

    // For images, use existing logic
    const baseParams = `limit=10&include=userLikes&images=1&views=1&likes=1&user=${_id}`;
    const generatorParam =
      generator !== 'all' ? `&generator=1&generatorId=${encodeURIComponent(generator)}` : '';
    const queryParamPart = queryParam ? `&searchQuery=${encodeURIComponent(queryParam)}` : '';
    const tabParams: { [key: string]: string } = {
      new: `&timespan=0${index === 0 ? '&date=1' : ''}`,
      hot: `&timespan=7${index === 0 ? '&sort=top' : ''}`,
      top: `&timespan=0${index === 0 ? '&sort=top' : ''}`
    };

    if (!tabParams[tab]) {
      throw new Error('Invalid tab provided');
    }

    let endpointResult = '';
    endpointResult = `${baseEndpoint}?${baseParams}${tabParams[tab]}${generatorParam}${queryParamPart}`;

    return endpointResult;
  };

  const getKey = useMemo(() => {
    return (pageIndex: any, previousPageData: any) => {
      // If the first page, no need to check the previous page data
      const tagId = pathname.split('/').pop();

      if (pageIndex === 0) {
        return constructUrl(currTab, currGenerators.join(','), pageIndex, tagId?.toString());
      }

      // Check if the previous page data has fewer items than expected
      if (previousPageData && previousPageData.results.length < 10) {
        return null;
      }

      // If previous page data is empty or it's the end of pagination, return null
      if (!previousPageData || !previousPageData.info.next || previousPageData.info.next === '') {
        return null;
      }

      // Return the URL for the next page
      return previousPageData.info.next;
    };
  }, [currTab, currGenerators, constructUrl, contentType]);

  const { data, error, setSize, size } = useSWRInfinite<PageData>(getKey, fetcher, {
    revalidateOnFocus: false, // Prevent revalidation on focus
    revalidateAll: false, // Prevent revalidation of all pages
    revalidateFirstPage: false // Prevent revalidation of the first page
  });
  const infiniteScrollBoundaryRef = useRef<HTMLDivElement | null>(null);
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (data && typeof data[size - 1] === 'undefined');
  const isReachingEnd =
    data &&
    (!data[data.length - 1]?.info.next ||
      data[data.length - 1]?.info.next === '' ||
      data[data.length - 1]?.results.length === 0);
  const posts = data // Flatten the nested array to a single array
    ? ([] as Post[]).concat(...data.map((page) => page.results))
    : [];
  let userName = '';
  if (data) {
    if (data.length > 0) {
      if (data[0].username) {
        userName = data[0].username;
      }
    }
  }

  // const nextPage = data && data[data.length - 1]?.info.next;
  const scrollToTop = () => {
    // This function will scroll the window to the top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSliderChange = (event: any) => {
    setSliderValue(event.target.value);
    // Serialize the cookie string using the `cookie` library
    const cookieName = contentType === 'gif' ? 'gifSliderValue' : 'imageSliderValue';
    const serializedCookie = cookie.serialize(cookieName, event.target.value, {
      path: '/',
      maxAge: 365 * 24 * 60 * 60 * 999 // Max age in seconds
    });
    // Set the cookie in the browser
    document.cookie = serializedCookie;

    if (contentType === 'gif') {
      // GIF layout settings
      switch (event.target.value) {
        case '0':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 2, lg: 2, md: 1, sm: 1 }
          });
          break;
        case '1':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 3, lg: 3, md: 2, sm: 2 }
          });
          break;
        case '2':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 4, lg: 4, md: 3, sm: 3 }
          });
          break;
        case '3':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 5, lg: 5, md: 4, sm: 4 }
          });
          break;
        default:
          console.error('Unexpected slider value for GIF');
      }
    } else {
      // Image layout settings
      switch (event.target.value) {
        case '0':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 3, lg: 2, md: 2, sm: 1 }
          });
          break;
        case '1':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 4, lg: 3, md: 2, sm: 2 }
          });
          break;
        case '2':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 5, lg: 4, md: 3, sm: 3 }
          });
          break;
        case '3':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 6, lg: 5, md: 4, sm: 4 }
          });
          break;
        case '4':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 7, lg: 6, md: 5, sm: 5 }
          });
          break;
        case '5':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 8, lg: 7, md: 6, sm: 5 }
          });
          break;
        case '6':
          dispatch({
            type: 'SET_VIEWING_MODE',
            payload: { default: 9, lg: 8, md: 7, sm: 6 }
          });
          break;
        default:
          console.error('Unexpected slider value for image');
      }
    }
  };

  const updateRangeValues = () => {
    const screenWidth = window.innerWidth;
    if (contentType === 'gif') {
      if (screenWidth < 768) {
        setMinValue(0);
        setMaxValue(2); // Mobile: 1-3 columns
      } else {
        setMinValue(0);
        setMaxValue(3); // Desktop: 2-5 columns
      }
    } else {
      // Images have full slider range
      if (screenWidth < 768) {
        // Mobile breakpoint
        setMinValue(0);
        setMaxValue(4);
      } else {
        setMinValue(0);
        setMaxValue(6);
      }
    }
  };

  function toggleFullScreen() {
    setFullScreen(!fullScreen);
    if (!document.fullscreenElement) {
      // Enter full screen
      document.documentElement.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable full-screen mode: ${err.message} (${err.name})`);
      });
    } else {
      // Exit full screen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  function toggleExpandContent() {
    // Serialize the cookie string using the `cookie` library
    const serializedCookie = cookie.serialize('expandContent', String(!state.expandContent), {
      path: '/',
      maxAge: 365 * 24 * 60 * 60 * 999 // Max age in seconds
    });
    // Set the cookie in the browser
    document.cookie = serializedCookie;
    dispatch({ type: 'SET_EXPAND_CONTENT', payload: '' });
  }

  useEffect(() => {
    updateRangeValues();

    const parsedCookies = cookie.parse(document.cookie);
    const cookieName = contentType === 'gif' ? 'gifSliderValue' : 'imageSliderValue';
    if (parsedCookies[cookieName]) {
      handleSliderChange({ target: { value: parsedCookies[cookieName] } });
    }

    if (parsedCookies.expandContent) {
      if (parsedCookies.expandContent === 'true') {
        dispatch({ type: 'SET_EXPAND_CONTENT', payload: true });
      }
    }

    window.addEventListener('resize', updateRangeValues);

    // Inline function for scroll event handling
    const handleScroll = () => {
      const shouldShow = window.scrollY > 200;
      setShowScrollTop(shouldShow);
    };
    // Setup the scroll listener
    window.addEventListener('scroll', handleScroll);
    // Inline function for intersection handling and observer setup
    let observer: IntersectionObserver | null = null;
    if (!isLoadingMore && !error && infiniteScrollBoundaryRef.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setSize(size + 1);
          }
        },
        { threshold: 1 }
      );
      observer.observe(infiniteScrollBoundaryRef.current);
    }

    // getUsernameById(_id).then((data: any) => {
    //   setTagData(data);
    // });
    // Cleanup function
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateRangeValues);
      observer?.disconnect();
    };
  }, [isLoadingMore, setSize, size, error, _id, contentType]);

  useEffect(() => {
    if (contentType === 'gif' && sliderValue > 3) {
      setSliderValue(3);
      dispatch({
        type: 'SET_VIEWING_MODE',
        payload: { default: 5, lg: 5, md: 3, sm: 3 }
      });
    }
  }, [contentType]);

  return (
    <div>
      <div className="mx-auto px-5 xl:max-w-[88rem]">
        {/* Images | GIFs Selector - Above username like in saved-posts */}
        {gifReady && (
          <div className="mx-auto -mt-4 flex flex-col sm:-mt-6">
            <div className="flex flex-row justify-center">
              <Link
                prefetch={false}
                href={{
                  pathname: newURL,
                  query: {
                    filter: currTab,
                    type: 'image'
                  }
                }}
                onClick={() => setContentType('image')}
                className={`rounded-md text-sm font-bold ${
                  contentType === 'image' ? 'text-white' : 'text-gray-300'
                }`}
              >
                <div className="flex items-center justify-center">
                  <ImageIcon
                    className={`mr-2 h-4 w-4 md:mr-3 ${
                      contentType === 'image' ? 'text-crp-red' : 'text-gray-300'
                    }`}
                  />
                  Images
                </div>
              </Link>
              <div className="mx-4 h-5 w-[1px] self-center bg-[#3C3C3C]" />
              <Link
                prefetch={false}
                href={{
                  pathname: newURL,
                  query: {
                    filter: currTab === 'hot' ? 'new' : currTab, // Reset to 'new' if hot since GIFs don't support hot
                    type: 'gif'
                  }
                }}
                onClick={() => {
                  setContentType('gif');
                  // Reset to 'new' if currently 'hot' since GIFs don't support hot
                  const newTab = currTab === 'hot' ? 'new' : currTab;
                  setCurrTab(newTab);
                }}
                className={`rounded-md text-sm font-bold ${
                  contentType === 'gif' ? 'text-white' : 'text-gray-300'
                }`}
              >
                <div className="flex items-center justify-center">
                  <Video
                    className={`mr-2 h-5 w-5 md:mr-3 ${
                      contentType === 'gif' ? 'text-crp-red' : 'text-gray-300'
                    }`}
                  />
                  GIFs
                </div>
              </Link>
            </div>
            <div className="relative left-[50%] right-[50%] mb-4 ml-[-50vw] mr-[-50vw] mt-2 w-[100vw] border-b-[0.5px] border-[#3C3C3C]" />
          </div>
        )}

        <CategoryText userName={userName} />

        <div className="z-10 flex items-center">
          <div className="xs:mx-0 z-10 mx-[-18px] flex items-center overflow-x-scroll whitespace-nowrap sm:overflow-hidden">
            {contentType === 'image' && (
              <div className="ml-5 flex">
                <TabLink
                  label="hot"
                  current={currTab}
                  setCurrent={setCurrTab}
                  newURL={newURL}
                  contentType={contentType}
                  translations={translations}
                />
              </div>
            )}
            <div className={contentType === 'gif' ? 'ml-5 flex' : 'flex'}>
              <TabLink
                label="new"
                current={currTab}
                setCurrent={setCurrTab}
                newURL={newURL}
                contentType={contentType}
                translations={translations}
              />
            </div>
            <TabLink
              label="top"
              current={currTab}
              setCurrent={setCurrTab}
              newURL={newURL}
              contentType={contentType}
              translations={translations}
            />
          </div>
        </div>
        <div className="mb-3.5 mt-2 flex flex-row items-center justify-center">
          <div className="flex-grow">
            <input
              type="range"
              aria-label="Slider"
              min={minValue}
              max={maxValue}
              value={sliderValue}
              onChange={handleSliderChange}
              className="h-2 w-full cursor-pointer appearance-none rounded-lg border-[0.5px] border-[#3C3C3C] bg-[#2B2D31]"
            />
          </div>
          <button
            onClick={toggleExpandContent}
            className={`${
              false ? '' : ''
            } ml-4 hidden flex-row overflow-hidden whitespace-nowrap rounded-md py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:flex`}
          >
            <Move color="white" className="mx-1 my-auto h-3.5 w-auto" />
            {state.expandContent ? translations.shrinkContent : translations.expandContent}
          </button>
          <button
            onClick={toggleFullScreen}
            className={`${
              false ? '' : ''
            } ml-4 hidden flex-row rounded-md py-[7px] text-sm font-medium text-white hover:brightness-105 focus:outline-none focus:ring-0 focus:ring-gray-300 sm:flex`}
          >
            <Expand color="white" className="mx-1 my-auto h-3.5 w-auto" />
            {fullScreen ? translations.exitFullScreen : translations.fullScreen}
          </button>
        </div>
      </div>
      <div
        className={`mx-auto mb-[100px] ${
          state.expandContent ? '' : 'px-0 sm:px-[7px] xl:max-w-[88rem]'
        }`}
      >
        {/* Images/GIFs */}
        <CardWrapper
          data={posts}
          contentType={contentType}
          modalTranslations={modalTranslations}
          signInModalTranslations={signInModalTranslations}
        />
        {/* Scroll to Top Icon */}
        {/* {showScrollTop && (
          <div className={`z-1000 fixed bottom-16 w-full ${(state.expandContent) ? "" : "px-5 md:px-10 xl:max-w-[88rem]"}`}>
            <button
              onClick={scrollToTop}
              className="ml-auto mr-8 flex animate-fade-up-sped rounded-full bg-[#DD0772] px-3 py-2 text-sm font-bold text-white hover:brightness-105 xl:mr-4 shadow-2xl"
            >
              <ChevronUp color="white" className="my-auto h-5 w-auto" />
            </button>
          </div>
        )} */}
        <div ref={infiniteScrollBoundaryRef} className="-mt-[55vh]" />
        {/* Loading Icon */}
        {isLoadingMore && !isReachingEnd && !error && posts.length % 10 === 0 && (
          <div className="flex">
            <Image
              src={'https://cdn6.imgpog.com/loadingGIF.webp'}
              width={50}
              height={50}
              alt="Loading"
              className="m-auto mb-[55vh] mt-[55vh]"
              unoptimized
            />
          </div>
        )}
      </div>
      <ScrollButton />
    </div>
  );
}
